import { JSX, ReactNode, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from "react-hot-toast";
import { Alert } from "react-bootstrap";
import Loading from "../../components/Loading";

/**
 * Checks whether an email or username already exists during sign-up.
 * Debounces the lookup by 300ms and returns an alert element to show to the user.
 * Supports resending the verification email directly from the alert.
 *
 * @param query - The email address or username to check.
 * @param checkDuplicate - Whether the duplicate check should be performed.
 */
export default function useCheckExistingUser({ query, checkDuplicate }: {
    /** The email or username value to check for duplicates. */
    query?: string;
    /** Set to true to activate the duplicate check. */
    checkDuplicate?: boolean;
}): { alert: JSX.Element } {
    const [extendedError, setExtendedError] = useState<ReactNode>('');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState<ReactNode>('');
    const [isSending, setSending] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (checkDuplicate && query) {
            setError('');
            timeoutRef.current = setTimeout(() => {
                const params = { query };
                axios.get('/users/check', { cancelToken: source.token, params }).then(({ data }) => {
                    const { status, resendVerificationUrl } = data.data;

                    const sendVerificationMail = () => {
                        setSending(true);
                        setExtendedError('');
                        axios.get(resendVerificationUrl, { cancelToken: source.token })
                            .then(({ data }) => {
                                setSuccess(data.message);
                            })
                            .catch((e) => {
                                setExtendedError(e.message);
                            })
                            .finally(() => setSending(false));
                    };

                    const common = (
                        <>
                            Click&nbsp;
                            <a className="fw-bold" onClick={sendVerificationMail}>here</a>
                            &nbsp;to resend the verification mail.
                        </>
                    );

                    switch (status) {
                        case 'EMAIL_EXISTS':
                            setError(<>This email already exists. {common}</>);
                            break;
                        case 'USERNAME_EXISTS':
                            setError(<>This username already exists. {common}</>);
                            break;
                    }
                }).catch((axiosError) => {
                    if (axiosError.message === 'cancelled' || axiosError.message === 'canceled') {
                        // Do not inform the user that the request was canceled
                        return;
                    } else if (axiosError.status !== 404) {
                        console.error(axiosError);
                        toast.error(axiosError.response?.data?.message || axiosError.message);
                    }
                });
            }, 300);
        }

        return () => {
            setSending(false);
            setSuccess('');
            clearTimeout(timeoutRef.current);
            source?.cancel();
        }
    }, [query, checkDuplicate]);

    return {
        alert: (
            <>
                {error && !success && <Alert variant="danger">{isSending ? <Loading /> : <>{error} {extendedError}</>}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}
            </>
        ),
    };
}
